// ════════════════════════════════════════════════════════════
// FÉRIAS E LICENÇAS — FUNÇÕES PURAS DE DATA (27/10/2026)
//
// Sem tela e sem servidor: dá para testar com node.
// Datas são SEMPRE texto 'AAAA-MM-DD'. As contas usam Date.UTC, nunca
// new Date('AAAA-MM-DD') (que em UTC-3 cai no dia anterior).
//
// Regras (decisão do Walber, 27/10/2026):
//  - Passado / agora / futuro é decidido SÓ pela data, nunca pelo tipo.
//  - Data de retorno = MESMA regra do Planner Férias (ver dataRetorno).
//  - Calendário "em campo": faltas e saída de turno NÃO entram;
//    oficializados e não-oficializados entram igual.
//  - Os alertas de conferência só aparecem na tela; não mudam o banco.
// ════════════════════════════════════════════════════════════

export interface RegistroAfast {
  id: number;
  agente_id: number;
  agente_nome?: string | null;
  tipo: string;
  data_inicio: string;
  data_fim: string;
  data_retorno?: string | null;
  oficializado?: boolean;
  motivo?: string | null;
}

export type Situacao = 'passado' | 'agora' | 'futuro';

// ── Aritmética de datas (texto AAAA-MM-DD) ──────────────────

function partes(iso: string): [number, number, number] {
  const [a, m, d] = iso.split('-').map(Number);
  return [a, m, d];
}

function deUTC(ms: number): string {
  const d = new Date(ms);
  const a = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dia = String(d.getUTCDate()).padStart(2, '0');
  return `${a}-${m}-${dia}`;
}

function msUTC(iso: string): number {
  const [a, m, d] = partes(iso);
  return Date.UTC(a, m - 1, d);
}

export function montarIso(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** Data de hoje no relógio do navegador (local, não UTC). */
export function hojeLocal(agora: Date = new Date()): string {
  return montarIso(agora.getFullYear(), agora.getMonth() + 1, agora.getDate());
}

export function somarDias(iso: string, n: number): string {
  return deUTC(msUTC(iso) + n * 86400000);
}

/** b − a, em dias. */
export function diasEntre(a: string, b: string): number {
  return Math.round((msUTC(b) - msUTC(a)) / 86400000);
}

/** 0 = domingo … 6 = sábado. */
export function diaDaSemana(iso: string): number {
  return new Date(msUTC(iso)).getUTCDay();
}

export function diasNoMes(ano: number, mes: number): number {
  return new Date(Date.UTC(ano, mes, 0)).getUTCDate();
}

// ── Feriados ────────────────────────────────────────────────
// Mesma lista do Planner Férias (app/routers/planner_ferias.py:56-58,
// FERIADOS_FIXOS + Sexta-feira Santa em feriados_do_ano, linhas 92-97).
// Inclui o 2 de Julho (Bahia). Feriados municipais não estão na lista.
// Os nomes são só para a tela; o conjunto de dias é o mesmo do Planner.

const FERIADOS_FIXOS: Array<[number, number, string]> = [
  [1, 1, 'Confraternização'],
  [4, 21, 'Tiradentes'],
  [5, 1, 'Dia do Trabalho'],
  [7, 2, 'Independência da Bahia'],
  [9, 7, 'Independência'],
  [10, 12, 'N. Sra. Aparecida'],
  [11, 2, 'Finados'],
  [11, 15, 'Proclamação da República'],
  [11, 20, 'Consciência Negra'],
  [12, 25, 'Natal'],
];

/** Domingo de Páscoa — cópia fiel de planner_ferias.py:71-86 (_pascoa). */
export function pascoa(ano: number): string {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return montarIso(ano, mes, dia);
}

const _cacheFeriados: Record<number, Record<string, string>> = {};

/** { 'AAAA-MM-DD': 'Nome' } dos feriados do ano. */
export function feriadosDoAno(ano: number): Record<string, string> {
  if (!_cacheFeriados[ano]) {
    const lista: Record<string, string> = {};
    FERIADOS_FIXOS.forEach(([m, d, nome]) => { lista[montarIso(ano, m, d)] = nome; });
    lista[somarDias(pascoa(ano), -2)] = 'Sexta-feira Santa';
    _cacheFeriados[ano] = lista;
  }
  return _cacheFeriados[ano];
}

export function nomeFeriado(iso: string): string | null {
  return feriadosDoAno(partes(iso)[0])[iso] || null;
}

/** Segunda a sexta e fora da lista de feriados (planner_ferias.py:100-101, dia_util). */
export function diaUtil(iso: string): boolean {
  const s = diaDaSemana(iso);
  return s !== 0 && s !== 6 && !nomeFeriado(iso);
}

/** Primeiro dia útil DEPOIS da data (planner_ferias.py:104-108, proximo_dia_util). */
export function proximoDiaUtil(depoisDe: string): string {
  let d = somarDias(depoisDe, 1);
  while (!diaUtil(d)) d = somarDias(d, 1);
  return d;
}

// ── Regras do módulo ────────────────────────────────────────

export function situacao(reg: RegistroAfast, hoje: string): Situacao {
  if (reg.data_fim < hoje) return 'passado';
  if (reg.data_inicio > hoje) return 'futuro';
  return 'agora';
}

/**
 * Dia em que o agente volta ao campo.
 * Cópia fiel de planner_ferias.py:177-178 (_item):
 *   volta = data_retorno se preenchida e posterior ao fim; senão, o primeiro dia útil depois do fim.
 */
export function dataRetorno(reg: RegistroAfast): string {
  if (reg.data_retorno && reg.data_retorno > reg.data_fim) return reg.data_retorno;
  return proximoDiaUtil(reg.data_fim);
}

export const TIPOS_FORA_DO_CALENDARIO = ['FALTA_JUSTIFICADA', 'FALTA_INJUSTIFICADA', 'SAIDA_TURNO'];

export function contaNoCalendario(reg: RegistroAfast): boolean {
  return TIPOS_FORA_DO_CALENDARIO.indexOf(reg.tipo) < 0;
}

/**
 * Agentes DISTINTOS fora no dia (um registro por agente — o de início mais antigo).
 * idsEquipe: quando informado, só conta agentes dessa lista (ex.: equipe de campo do Planner).
 */
export function foraNoDia(registros: RegistroAfast[], dia: string, idsEquipe?: number[]): RegistroAfast[] {
  const porAgente: Record<number, RegistroAfast> = {};
  registros.forEach(r => {
    if (!contaNoCalendario(r)) return;
    if (idsEquipe && idsEquipe.indexOf(r.agente_id) < 0) return;
    if (r.data_inicio > dia || r.data_fim < dia) return;
    const atual = porAgente[r.agente_id];
    if (!atual || r.data_inicio < atual.data_inicio || (r.data_inicio === atual.data_inicio && r.id < atual.id)) {
      porAgente[r.agente_id] = r;
    }
  });
  return Object.keys(porAgente)
    .map(k => porAgente[Number(k)])
    .sort((a, b) => (a.agente_nome || '').localeCompare(b.agente_nome || ''));
}

export const TIPOS_FERIAS = ['FERIAS_PLANEJADAS', 'FERIAS_GOZADAS'];

const ROTULOS: Record<string, string> = {
  FERIAS_PLANEJADAS: 'Férias',
  FERIAS_GOZADAS: 'Férias',
  LICENCA_MEDICA: 'Licença médica',
  LICENCA_MATERNIDADE: 'Licença maternidade',
  LICENCA_OUTROS: 'Outras licenças',
  LICENCA_PREMIO: 'Licença prêmio',
  FALTA_JUSTIFICADA: 'Falta justificada',
  FALTA_INJUSTIFICADA: 'Falta injustificada',
  SAIDA_TURNO: 'Saída durante o turno',
};

export function rotuloTipo(tipo: string): string {
  return ROTULOS[tipo] || tipo;
}

// ── Pílulas de tipo (filtro) ────────────────────────────────
// Traduzem para a mesma lista de códigos que o filtro antigo mandava ao motor.

export type GrupoTipo = 'todos' | 'ferias' | 'premio' | 'outros';

export const GRUPOS_TIPO: Array<{ valor: GrupoTipo; rotulo: string }> = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'ferias', rotulo: 'Férias' },
  { valor: 'premio', rotulo: 'Licença prêmio' },
  { valor: 'outros', rotulo: 'Outros afastamentos' },
];

export function codigosDoGrupo(grupo: GrupoTipo, todosCodigos: string[]): string[] {
  const codigos = todosCodigos.length ? todosCodigos : Object.keys(ROTULOS);
  if (grupo === 'ferias') return TIPOS_FERIAS.slice();
  if (grupo === 'premio') return ['LICENCA_PREMIO'];
  if (grupo === 'outros') return codigos.filter(c => TIPOS_FERIAS.indexOf(c) < 0 && c !== 'LICENCA_PREMIO');
  return [];
}

export function grupoDosCodigos(selecionados: string[], todosCodigos: string[]): GrupoTipo | null {
  const chave = (l: string[]) => l.slice().sort().join(',');
  const atual = chave(selecionados);
  for (const g of GRUPOS_TIPO) {
    if (chave(codigosDoGrupo(g.valor, todosCodigos)) === atual) return g.valor;
  }
  return null;
}

/** Avisos de conferência por id do registro. Só leitura: não muda o banco. */
export function alertasConferir(registros: RegistroAfast[], hoje: string): Record<number, string[]> {
  const saida: Record<number, string[]> = {};
  const add = (id: number, txt: string) => { (saida[id] = saida[id] || []).push(txt); };

  const grupos: Record<string, number[]> = {};
  registros.forEach(r => {
    const chave = `${r.agente_id}|${r.tipo}|${r.data_inicio}|${r.data_fim}`;
    (grupos[chave] = grupos[chave] || []).push(r.id);
  });
  Object.keys(grupos).forEach(chave => {
    const ids = grupos[chave];
    if (ids.length > 1) ids.forEach(id => add(id, `Registrado ${ids.length} vezes`));
  });

  registros.forEach(r => {
    const s = situacao(r, hoje);
    if (r.tipo === 'FERIAS_PLANEJADAS' && s === 'passado') add(r.id, 'Ainda marcado como "planejada"');
    if (r.tipo === 'FERIAS_GOZADAS' && s === 'futuro') add(r.id, 'Marcado como "gozada", mas ainda não aconteceu');
  });
  return saida;
}

// ── Calendário do mês ───────────────────────────────────────

export interface DiaCalendario {
  iso: string;
  dia: number;
  util: boolean;
  feriado: string | null;
  fora: RegistroAfast[];
  emCampo: number;
}

export interface ResumoMes {
  dias: DiaCalendario[];
  menor: number | null;        // menor "em campo" num dia útil (null = mês sem dia útil)
  menorDe: string | null;      // primeiro dia útil com o menor número
  menorAte: string | null;     // último dia útil com o menor número
}

export function resumoMes(registros: RegistroAfast[], ano: number, mes: number, total: number, idsEquipe?: number[]): ResumoMes {
  const dias: DiaCalendario[] = [];
  let menor: number | null = null;
  let menorDe: string | null = null;
  let menorAte: string | null = null;
  for (let d = 1; d <= diasNoMes(ano, mes); d++) {
    const iso = montarIso(ano, mes, d);
    const feriado = nomeFeriado(iso);
    const util = diaUtil(iso);
    const fora = foraNoDia(registros, iso, idsEquipe);
    const emCampo = Math.max(0, total - fora.length);
    dias.push({ iso, dia: d, util, feriado, fora, emCampo });
    if (util) {
      if (menor === null || emCampo < menor) { menor = emCampo; menorDe = iso; menorAte = iso; }
      else if (emCampo === menor) { menorAte = iso; }
    }
  }
  return { dias, menor, menorDe, menorAte };
}

// ── Formatação ──────────────────────────────────────────────

export const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
export const MESES_CURTOS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
export const SEMANA_CURTA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
export const SEMANA_LONGA = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

/** dd/mm/aa */
export function fmtCurta(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a.slice(2)}`;
}

/** dd/mm/aaaa */
export function fmtLonga(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}

/** dd/mm */
export function fmtDM(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

/** "seg 03/05/2027" */
export function fmtSemLonga(iso: string): string {
  return `${SEMANA_CURTA[diaDaSemana(iso)]} ${fmtLonga(iso)}`;
}

/** "seg 03/05" */
export function fmtSemDM(iso: string): string {
  return `${SEMANA_CURTA[diaDaSemana(iso)]} ${fmtDM(iso)}`;
}

/** "em 7 dias" / "amanhã" / "hoje" */
export function fmtEmDias(n: number): string {
  if (n <= 0) return 'hoje';
  if (n === 1) return 'amanhã';
  return `em ${n} dias`;
}

/** "Ana", "Ana e Bia", "Ana, Bia e Caio" */
export function juntarNomes(nomes: string[]): string {
  if (nomes.length <= 1) return nomes.join('');
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}
